import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { investorService, commercialManagerService } from '../../services/userService';
import { Investor, CommercialManager } from '../../types/User';
import './UserForm.css';

interface InvestorFormProps {
  investor: Investor | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const InvestorForm: React.FC<InvestorFormProps> = ({ investor, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<Investor>({
    nombre: '',
    nif: '',
    email: '',
    telefono: '',
    direccion: '',
    titularCuentaPrincipal: '',
    ibanPrincipal: '',
    responsableComercialId: '',
    procedencia: '',
    estado: '',
    expiracionDni: '',
    fechaNacimiento: '',
    estadoCivil: '',
    profesion: '',
    regimenLaboral: '',
    cargoPublico: false,
  });
  const [commercialManagers, setCommercialManagers] = useState<CommercialManager[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadCommercialManagers = async () => {
      try {
        const response = await commercialManagerService.getAll();
        setCommercialManagers(response.data);
      } catch (err) {
        console.error('Error loading commercial managers:', err);
      }
    };

    loadCommercialManagers();

    if (investor) {
      setFormData(investor);
    }
  }, [investor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'cargoPublico' ? value === 'true' : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (investor?.id) {
        await investorService.update(investor.id, formData);
        showToast('Inversor actualizado correctamente');
      } else {
        await investorService.create(formData);
        showToast('Inversor creado correctamente');
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar el inversor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-form">
      <h3>{investor ? 'Editar Inversor' : 'Crear Inversor'}</h3>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <h4>Identificación</h4>
        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="nombre">Nombre Completo / Razón Social *</label>
            <input
              type="text"
              id="nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label htmlFor="nif">DNI / CIF *</label>
            <input
              type="text"
              id="nif"
              name="nif"
              value={formData.nif}
              onChange={handleChange}
              required
              maxLength={20}
            />
          </div>
        </div>

        <h4>Contacto</h4>
        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="email">Email Principal *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label htmlFor="telefono">Teléfono</label>
            <input
              type="tel"
              id="telefono"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              maxLength={20}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="direccion">Dirección</label>
            <textarea
              id="direccion"
              name="direccion"
              value={formData.direccion}
              onChange={handleChange}
              rows={2}
              maxLength={500}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="titularCuentaPrincipal">Titular Cuenta Principal</label>
            <input
              type="text"
              id="titularCuentaPrincipal"
              name="titularCuentaPrincipal"
              value={formData.titularCuentaPrincipal}
              onChange={handleChange}
              maxLength={255}
            />
          </div>
          <div className="form-group">
            <label htmlFor="ibanPrincipal">IBAN Principal</label>
            <input
              type="text"
              id="ibanPrincipal"
              name="ibanPrincipal"
              value={formData.ibanPrincipal}
              onChange={handleChange}
              maxLength={34}
            />
          </div>
        </div>

        <h4>CRM y Comercial</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="responsableComercialId">Responsable Comercial</label>
            <select
              id="responsableComercialId"
              name="responsableComercialId"
              value={formData.responsableComercialId || ''}
              onChange={handleChange}
            >
              <option value="">Sin asignar</option>
              {commercialManagers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="procedencia">Procedencia</label>
            <select
              id="procedencia"
              name="procedencia"
              value={formData.procedencia}
              onChange={handleChange}
            >
              <option value="">Seleccionar...</option>
              <option value="Web">Web</option>
              <option value="RRSS">Redes Sociales</option>
              <option value="Referido">Referido</option>
              <option value="Evento">Evento</option>
              <option value="Publicidad">Publicidad</option>
              <option value="Otro">Otro</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="estado">Estado</label>
            <select
              id="estado"
              name="estado"
              value={formData.estado}
              onChange={handleChange}
            >
              <option value="">Seleccionar...</option>
              <option value="Prospecto">Prospecto</option>
              <option value="Activo">Activo</option>
              <option value="Histórico">Histórico</option>
            </select>
          </div>
        </div>

        <h4>Cumplimiento</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaNacimiento">Fecha de Nacimiento</label>
            <input
              type="date"
              id="fechaNacimiento"
              name="fechaNacimiento"
              value={formData.fechaNacimiento}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="expiracionDni">Expiración DNI</label>
            <input
              type="date"
              id="expiracionDni"
              name="expiracionDni"
              value={formData.expiracionDni}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="estadoCivil">Estado Civil</label>
            <select
              id="estadoCivil"
              name="estadoCivil"
              value={formData.estadoCivil}
              onChange={handleChange}
            >
              <option value="">Seleccionar...</option>
              <option value="Soltero/a">Soltero/a</option>
              <option value="Casado/a">Casado/a</option>
              <option value="Divorciado/a">Divorciado/a</option>
              <option value="Viudo/a">Viudo/a</option>
              <option value="Pareja de hecho">Pareja de hecho</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="profesion">Profesión</label>
            <input
              type="text"
              id="profesion"
              name="profesion"
              value={formData.profesion}
              onChange={handleChange}
              maxLength={255}
            />
          </div>
          <div className="form-group">
            <label htmlFor="regimenLaboral">Régimen Laboral</label>
            <select
              id="regimenLaboral"
              name="regimenLaboral"
              value={formData.regimenLaboral}
              onChange={handleChange}
            >
              <option value="">Seleccionar...</option>
              <option value="Por cuenta propia">Por cuenta propia</option>
              <option value="Por cuenta ajena">Por cuenta ajena</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="cargoPublico">Cargo Público</label>
            <select
              id="cargoPublico"
              name="cargoPublico"
              value={formData.cargoPublico ? 'true' : 'false'}
              onChange={handleChange}
            >
              <option value="false">No</option>
              <option value="true">Sí</option>
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : investor ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvestorForm;
